import { Location } from '@angular/common';
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { App } from '@capacitor/app';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationExtras,
  NavigationSkipped,
  NavigationStart,
  ActivatedRouteSnapshot,
  Router,
  UrlTree,
} from '@angular/router';
import { NavController, Platform } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PlatformService } from '@services/platform.service';
import { ToastService } from '@services/toast.service';
import {
  isInternalStateRouteUrl,
  normalizeStateRoutePath,
  normalizeStateRouteUrl,
  resolveStateRouteFallbackUrl,
  STATE_ROUTE_ROOT,
} from '@state-routing/config/state-routing.config';
import {
  createStateHistoryRouterState,
  readStateHistoryRouterPosition,
  setStateHistoryGuard,
  StateHistoryRouterPosition,
  StateHistoryTraversalResult,
  traverseStateHistory,
} from '@state-routing/services/state-history-guard';
import { StateOverlayService } from '@state-routing/services/state-overlay.service';

interface AppNavigationOptions extends NavigationExtras {
  animated?: boolean;
}

interface ExternalEntryNavigationOptions extends AppNavigationOptions {
  readonly coldStart: boolean;
  readonly fallbackUrl?: string;
}

type NavigationPopOutcome = 'cancelled' | 'moved' | 'unavailable';
type BrowserRootBoundaryState = 'active' | 'base';

interface HistoryCollapseResult {
  readonly completed: boolean;
  readonly interrupted: boolean;
  readonly originalPageId: number | null;
  readonly originalUrl: string;
  readonly popCount: number;
}

@Injectable()
export class AppNavigationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly location = inject(Location);
  private readonly navController = inject(NavController);
  private readonly ngZone = inject(NgZone);
  private readonly platform = inject(Platform);
  private readonly platformService = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly stateOverlay = inject(StateOverlayService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private static readonly ANDROID_BACK_CONFIRMATION_MS: number = 2_000;
  private static readonly ANDROID_BACK_TOAST_ID: string = 'android-back-confirmation';
  private static readonly ANDROID_BACK_TRANSLATION_KEY: string = 'appNavigation.doubleBackToMinimize';
  private static readonly DEPRECATED_BROWSER_STATE_KEY: string = 'stateRouteBrowserEntry';
  private static readonly DEPRECATED_STORAGE_KEY: string = 'state-route-stack:v1';
  private static readonly MAX_ORIGIN_POP_COUNT: number = 16;
  private static readonly COMPLETED_FLOW_IDS_STATE_KEY: string = 'appNavigationCompletedFlowIds';
  private static readonly ENTRY_FALLBACK_URL_STATE_KEY: string = 'appNavigationEntryFallbackUrl';
  private static readonly FLOW_ID_STATE_KEY: string = 'appNavigationFlowId';
  private static readonly NAVIGATION_FROM_STATE_KEY: string = 'stateNavigationFromUrl';
  private static readonly POP_NAVIGATION_TIMEOUT_MS: number = 2_000;
  private static readonly ROUTE_FLOW_DATA_KEY: string = 'navigationFlowRoot';
  private static readonly ROOT_BOUNDARY_STATE_KEY: string = 'appNavigationRootBoundary';

  private readonly completedFlowIds = new Set<string>();
  private androidBackConfirmationExpiresAt: number = 0;
  private browserRootBoundaryInitialized: boolean = false;
  private explicitNavigationDepth: number = 0;
  private flowForwardRejectionOperation: Promise<void> | null = null;
  private hardwareBackTail: Promise<void> = Promise.resolve();
  private navigationTail: Promise<void> = Promise.resolve();
  private pendingNavigationOrigins = new Map<number, string>();
  private userBackOperation: Promise<boolean> | null = null;
  private hardwareBackSubscription: Subscription | null = null;
  private routeTrackingSubscription: Subscription | null = null;
  private started: boolean = false;

  public get isExplicitNavigationInProgress(): boolean {
    return this.explicitNavigationDepth > 0;
  }

  public start(): void {
    if (this.started) return;

    this.started = true;
    this.clearDeprecatedStateRouterData();
    this.initializeBrowserRootBoundary();
    this.readCompletedFlowIds(this.copyHistoryState()).forEach((flowId: string): void => {
      this.completedFlowIds.add(flowId);
    });
    this.syncRouteFlow();
    this.stateOverlay.start();
    setStateHistoryGuard(
      (event: PopStateEvent): boolean => this.interceptBrowserPopState(event),
    );
    this.hardwareBackSubscription = this.platform.backButton.subscribeWithPriority(10, (): void => {
      this.enqueueHardwareBack();
    });
    this.routeTrackingSubscription = this.router.events.subscribe((event: RouterEvent): void => {
      this.trackNavigationOrigin(event);
    });
    this.destroyRef.onDestroy((): void => {
      this.hardwareBackSubscription?.unsubscribe();
      this.hardwareBackSubscription = null;
      this.routeTrackingSubscription?.unsubscribe();
      this.routeTrackingSubscription = null;
      this.pendingNavigationOrigins.clear();
      this.completedFlowIds.clear();
      this.browserRootBoundaryInitialized = false;
      setStateHistoryGuard(null);
    });
  }

  public navigateForward(
    url: string | UrlTree | unknown[],
    options: AppNavigationOptions = {},
    canNavigate?: () => boolean,
  ): Promise<boolean> {
    return this.enqueue((): Promise<boolean> => this.runExplicitNavigation(async (): Promise<boolean> => {
      if (canNavigate?.() === false) return false;
      if (!await this.stateOverlay.closeAll()) return false;
      if (canNavigate?.() === false) return false;
      return await this.navController.navigateForward(url, this.withNavigationOrigin(options));
    }));
  }

  public getCurrentEntryOriginUrl(): string | null {
    return this.resolveNavigationOriginUrl();
  }

  public navigateFromExternalEntry(
    url: string | UrlTree | unknown[],
    options: ExternalEntryNavigationOptions,
  ): Promise<boolean> {
    const {coldStart, fallbackUrl, ...navigationOptions} = options;
    if (!coldStart) return this.navigateForward(url, navigationOptions);

    return this.enqueue((): Promise<boolean> => this.runExplicitNavigation(async (): Promise<boolean> => {
      if (!await this.stateOverlay.closeAll()) return false;

      const targetUrl: string = this.resolveNavigationTargetUrl(url, navigationOptions);
      const resolvedFallbackUrl: string = fallbackUrl && isInternalStateRouteUrl(fallbackUrl)
        ? normalizeStateRouteUrl(fallbackUrl)
        : resolveStateRouteFallbackUrl(targetUrl) ?? STATE_ROUTE_ROOT;
      const state: Record<string, unknown> = this.isRecord(navigationOptions.state)
        ? {...navigationOptions.state}
        : {};
      delete state[AppNavigationService.FLOW_ID_STATE_KEY];
      delete state[AppNavigationService.NAVIGATION_FROM_STATE_KEY];
      state[AppNavigationService.ENTRY_FALLBACK_URL_STATE_KEY] = resolvedFallbackUrl;
      this.writeCompletedFlowIds(state);

      const originalUrl: string = this.resolveCurrentUrl();
      const navigated: boolean = await this.navController.navigateRoot(url, {
        ...navigationOptions,
        state,
        replaceUrl: true,
      });
      const currentUrl: string = this.resolveCurrentUrl();
      if (navigated
        || !this.isTargetUrl(currentUrl, originalUrl)
        || this.isTargetUrl(currentUrl, targetUrl)) {
        this.writeEntryFallbackUrl(resolvedFallbackUrl);
      }
      if (navigated || !this.isTargetUrl(currentUrl, targetUrl)) return navigated;

      return true;
    }));
  }

  public navigateRoot(
    url: string | UrlTree | unknown[],
    options: AppNavigationOptions = {},
  ): Promise<boolean> {
    return this.enqueue((): Promise<boolean> => this.runExplicitNavigation(async (): Promise<boolean> => {
      if (!await this.stateOverlay.closeAll()) return false;
      return await this.navController.navigateRoot(url, options);
    }));
  }

  public navigateToSection(
    url: string,
    options: AppNavigationOptions = {},
  ): Promise<boolean> {
    return this.enqueue((): Promise<boolean> => this.runExplicitNavigation(async (): Promise<boolean> => {
      if (!await this.stateOverlay.closeAll()) return false;

      const targetUrl: string = normalizeStateRouteUrl(url);
      if (this.isTargetUrl(this.resolveCurrentUrl(), targetUrl)) return false;

      const popOutcome: NavigationPopOutcome = await this.tryPopToNavigationOrigin(targetUrl, options);
      if (popOutcome === 'moved') return true;
      if (popOutcome === 'cancelled') return false;

      const rootPopOutcome: NavigationPopOutcome = await this.tryPopToNavigationOrigin(
        STATE_ROUTE_ROOT,
        options,
        targetUrl,
      );
      if (rootPopOutcome !== 'unavailable') return rootPopOutcome === 'moved';

      const state: Record<string, unknown> = this.isRecord(options.state) ? {...options.state} : {};
      delete state[AppNavigationService.NAVIGATION_FROM_STATE_KEY];
      delete state[AppNavigationService.FLOW_ID_STATE_KEY];
      state[AppNavigationService.ENTRY_FALLBACK_URL_STATE_KEY] = STATE_ROUTE_ROOT;
      return await this.navController.navigateRoot(targetUrl, {...options, state, replaceUrl: true});
    }));
  }

  public redirect(
    url: string | UrlTree | unknown[],
    options: AppNavigationOptions = {},
    canNavigate?: () => boolean,
  ): Promise<boolean> {
    return this.enqueue((): Promise<boolean> => this.runExplicitNavigation(async (): Promise<boolean> => {
      if (canNavigate?.() === false) return false;
      if (!await this.stateOverlay.closeAll()) return false;
      if (canNavigate?.() === false) return false;
      return await this.navController.navigateBack(url, {...options, replaceUrl: true});
    }));
  }

  public returnTo(
    url: string,
    options: AppNavigationOptions = {},
  ): Promise<boolean> {
    return this.enqueue((): Promise<boolean> => this.runExplicitNavigation(async (): Promise<boolean> => {
      if (!await this.stateOverlay.closeAll()) return false;
      return await this.returnToRoute(url, options);
    }));
  }

  public back(
    fallbackUrl?: string,
    options: AppNavigationOptions = {},
  ): Promise<boolean> {
    if (this.userBackOperation) return this.userBackOperation;

    const operation: Promise<boolean> = this.enqueue(async (): Promise<boolean> => {
      if (await this.stateOverlay.closeTop()) return true;
      return await this.popRoute(fallbackUrl, options);
    });
    this.userBackOperation = operation;
    const release = (): void => {
      if (this.userBackOperation === operation) {
        this.userBackOperation = null;
      }
    };
    void operation.then(release, release);
    return operation;
  }

  public replaceCurrentRoute(url: string): boolean {
    if (this.router.getCurrentNavigation()) return false;

    const normalizedUrl: string = normalizeStateRouteUrl(url);
    this.location.replaceState(normalizedUrl, undefined, this.copyHistoryState());
    return true;
  }

  public completeFlow(
    url: string,
    flowRootUrl: string,
    options: AppNavigationOptions = {},
  ): Promise<boolean> {
    return this.enqueue((): Promise<boolean> => this.runExplicitNavigation(async (): Promise<boolean> => {
      if (!await this.stateOverlay.closeAll()) return false;

      const flowId: string = normalizeStateRoutePath(flowRootUrl);
      const collapseResult: HistoryCollapseResult = await this.collapseHistory(
        (): boolean => this.resolveCurrentFlowId() !== flowId || !this.resolveNavigationOriginUrl(),
      );
      if (!collapseResult.completed) {
        if (!collapseResult.interrupted) {
          await this.restoreCollapsedHistory(collapseResult);
        }
        return false;
      }

      const flowAlreadyCompleted: boolean = this.completedFlowIds.has(flowId);
      this.completedFlowIds.add(flowId);

      const targetUrl: string = this.resolveReturnTargetUrl(url, options);
      const state: Record<string, unknown> = this.isRecord(options.state) ? {...options.state} : {};
      delete state[AppNavigationService.FLOW_ID_STATE_KEY];
      const entryFallbackUrl: string | null = this.resolveEntryFallbackUrl();
      if (entryFallbackUrl) {
        state[AppNavigationService.ENTRY_FALLBACK_URL_STATE_KEY] = entryFallbackUrl;
      }
      this.writeCompletedFlowIds(state);
      const replaceUrl: boolean = this.resolveCurrentFlowId() === flowId
        || this.isTargetUrl(this.resolveCurrentUrl(), targetUrl);
      try {
        const navigated: boolean = await this.navController.navigateRoot(targetUrl, {
          ...options,
          state,
          replaceUrl,
        });
        if (navigated) return true;
        if (this.isTargetUrl(this.resolveCurrentUrl(), targetUrl)) {
          const currentState: Record<string, unknown> = {
            ...this.copyHistoryState(),
            ...state,
          };
          delete currentState[AppNavigationService.FLOW_ID_STATE_KEY];
          this.location.replaceState(targetUrl, undefined, currentState);
          return true;
        }

        if (!flowAlreadyCompleted) this.completedFlowIds.delete(flowId);
        await this.restoreCollapsedHistory(collapseResult);
        return false;
      } catch (error) {
        if (!flowAlreadyCompleted) this.completedFlowIds.delete(flowId);
        await this.restoreCollapsedHistory(collapseResult);
        throw error;
      }
    }));
  }

  private async collapseHistory(
    isTargetEntry: () => boolean,
    canTraverse: (currentUrl: string, originUrl: string) => boolean = (): boolean => true,
  ): Promise<HistoryCollapseResult> {
    const originalUrl: string = this.resolveCurrentUrl();
    const originalPageId: number | null = readStateHistoryRouterPosition(this.copyHistoryState())?.pageId ?? null;
    let popCount: number = 0;
    for (; popCount < AppNavigationService.MAX_ORIGIN_POP_COUNT; popCount += 1) {
      if (isTargetEntry()) {
        return {completed: true, interrupted: false, originalPageId, originalUrl, popCount};
      }

      const originUrl: string | null = this.resolveNavigationOriginUrl();
      if (!originUrl || !canTraverse(this.resolveCurrentUrl(), originUrl)) {
        return {completed: false, interrupted: false, originalPageId, originalUrl, popCount};
      }

      const traversalResult: StateHistoryTraversalResult = await traverseStateHistory(
        -1,
        (): boolean => this.isTargetUrl(this.resolveCurrentUrl(), originUrl),
      );
      if (traversalResult !== 'expected') {
        console.warn('[AppNavigation] failed to collapse a history entry', {traversalResult});
        return {
          completed: false,
          interrupted: traversalResult === 'unexpected',
          originalPageId,
          originalUrl,
          popCount,
        };
      }
    }

    return {
      completed: isTargetEntry(),
      interrupted: false,
      originalPageId,
      originalUrl,
      popCount,
    };
  }

  private async restoreCollapsedHistory(result: HistoryCollapseResult): Promise<void> {
    if (result.popCount === 0 || this.isOriginalHistoryEntry(result)) return;

    const traversalResult: StateHistoryTraversalResult = await traverseStateHistory(
      result.popCount,
      (): boolean => this.isOriginalHistoryEntry(result),
    );
    if (traversalResult !== 'expected') {
      console.warn('[AppNavigation] failed to restore an interrupted history traversal', {traversalResult});
    }
  }

  private isOriginalHistoryEntry(result: HistoryCollapseResult): boolean {
    return this.resolveCurrentUrl() === result.originalUrl
      && (result.originalPageId === null
        || readStateHistoryRouterPosition(this.copyHistoryState())?.pageId === result.originalPageId);
  }

  private async waitForHistoryRestoration(result: HistoryCollapseResult): Promise<void> {
    if (typeof window === 'undefined' || this.isOriginalHistoryEntry(result)) return;

    // Angular restores rejected computed navigations with an asynchronous history.go().
    // Wait for its router event too: popstate alone precedes Angular's scheduled navigation.
    await new Promise<void>((resolve): void => {
      const finish = (): void => {
        clearTimeout(timeoutId);
        subscription.unsubscribe();
        resolve();
      };
      const timeoutId: ReturnType<typeof setTimeout> = setTimeout((): void => {
        console.warn('[AppNavigation] cancelled navigation did not restore its history entry');
        finish();
      }, AppNavigationService.POP_NAVIGATION_TIMEOUT_MS);
      const subscription: Subscription = this.router.events.subscribe((event: RouterEvent): void => {
        if ((event instanceof NavigationEnd || event instanceof NavigationSkipped)
          && this.isOriginalHistoryEntry(result)) finish();
      });
    });
  }

  private interceptBrowserPopState(event: PopStateEvent): boolean {
    if (this.stateOverlay.interceptBrowserPopState(event)) return true;

    if (this.readBrowserRootBoundaryState(event.state) === 'base') {
      if (this.isTargetUrl(this.router.url, STATE_ROUTE_ROOT)) {
        this.activateBrowserRootBoundary();
        return true;
      }

      return false;
    }

    const flowId: string | null = this.readFlowId(event.state);
    if (!flowId || !this.completedFlowIds.has(flowId)) return false;

    this.ngZone.run((): void => {
      void this.rejectCompletedFlowEntry(flowId);
    });
    return true;
  }

  private async handleHardwareBack(): Promise<void> {
    const handled: boolean = await this.back();
    if (
      handled
      || !this.platformService.isAndroidApp
      || !this.isTargetUrl(this.resolveCurrentUrl(), STATE_ROUTE_ROOT)
    ) {
      await this.resetAndroidBackConfirmation();
      return;
    }

    const now: number = Date.now();
    if (now <= this.androidBackConfirmationExpiresAt) {
      await this.resetAndroidBackConfirmation();
      await App.minimizeApp();
      return;
    }

    this.androidBackConfirmationExpiresAt = now + AppNavigationService.ANDROID_BACK_CONFIRMATION_MS;
    const message: string = this.translate.instant(AppNavigationService.ANDROID_BACK_TRANSLATION_KEY);
    await this.toastService.info(message, {
      color: undefined,
      cssClass: 'android-back-toast',
      duration: AppNavigationService.ANDROID_BACK_CONFIRMATION_MS,
      id: AppNavigationService.ANDROID_BACK_TOAST_ID,
      position: 'bottom',
    });
  }

  private enqueueHardwareBack(): void {
    this.hardwareBackTail = this.hardwareBackTail
      .then((): Promise<void> => this.handleHardwareBack())
      .catch((error: unknown): void => {
        console.error('[AppNavigation] hardware Back failed', error);
      });
  }

  private async resetAndroidBackConfirmation(): Promise<void> {
    if (this.androidBackConfirmationExpiresAt === 0) return;

    this.androidBackConfirmationExpiresAt = 0;
    await this.toastService.dismissById(AppNavigationService.ANDROID_BACK_TOAST_ID);
  }

  private async rejectCompletedFlowEntry(flowId: string): Promise<void> {
    if (this.flowForwardRejectionOperation) {
      await this.flowForwardRejectionOperation;
      return;
    }

    const operation: Promise<void> = this.performCompletedFlowEntryRejection(flowId);
    this.flowForwardRejectionOperation = operation;
    try {
      await operation;
    } finally {
      if (this.flowForwardRejectionOperation === operation) {
        this.flowForwardRejectionOperation = null;
      }
    }
  }

  private async performCompletedFlowEntryRejection(flowId: string): Promise<void> {
    for (let popCount: number = 0; popCount < AppNavigationService.MAX_ORIGIN_POP_COUNT; popCount += 1) {
      if (this.resolveCurrentFlowId() !== flowId) return;

      const traversalResult: StateHistoryTraversalResult = await traverseStateHistory(
        -1,
        (): boolean => true,
      );
      if (traversalResult !== 'expected') {
        console.warn('[AppNavigation] failed to reject browser Forward into a completed flow', {
          flowId,
          traversalResult,
        });
        return;
      }
    }

    console.warn('[AppNavigation] completed flow rejection exceeded the history traversal limit', {flowId});
  }

  private enqueue(action: () => Promise<boolean>): Promise<boolean> {
    const operation: Promise<boolean> = this.navigationTail.then(action, action);
    this.navigationTail = operation.then(
      (): void => undefined,
      (): void => undefined,
    );
    return operation;
  }

  private async popRoute(
    fallbackUrl: string | undefined,
    options: AppNavigationOptions,
  ): Promise<boolean> {
    const currentUrl: string = this.resolveCurrentUrl();
    const outcome: NavigationPopOutcome = await this.tryPopRoute(options);
    if (outcome === 'moved') return true;
    if (outcome === 'cancelled') return false;

    const targetUrl: string = this.resolveBackFallback(currentUrl, fallbackUrl);
    if (targetUrl === currentUrl) return false;
    return await this.navController.navigateBack(targetUrl, {...options, replaceUrl: true});
  }

  private async returnToRoute(url: string, options: AppNavigationOptions): Promise<boolean> {
    const targetUrl: string = this.resolveReturnTargetUrl(url, options);
    if (this.isTargetUrl(this.resolveCurrentUrl(), targetUrl)) return false;
    const popOutcome: NavigationPopOutcome = await this.tryPopToNavigationOrigin(targetUrl, options);
    if (popOutcome === 'moved') return true;
    if (popOutcome === 'cancelled') return false;
    return await this.navController.navigateBack(
      targetUrl,
      this.withReturnNavigationOrigin(targetUrl, options),
    );
  }

  private async tryPopToNavigationOrigin(
    targetUrl: string,
    options: AppNavigationOptions,
    destinationUrl: string = targetUrl,
  ): Promise<NavigationPopOutcome> {
    const collapseResult: HistoryCollapseResult = await this.collapseHistory(
      (): boolean => this.isTargetUrl(this.resolveCurrentUrl(), targetUrl),
      (currentUrl: string, originUrl: string): boolean => (
        this.canPopOriginTowardsTarget(currentUrl, originUrl, targetUrl)
      ),
    );
    if (!collapseResult.completed) {
      if (collapseResult.interrupted) return 'cancelled';
      await this.restoreCollapsedHistory(collapseResult);
      return 'unavailable';
    }

    // Traverse native history without activating intermediate Angular/Ionic pages.
    // Only this final navigation runs guards and changes the visible page.
    const restoredUrl: string = this.resolveCurrentUrl();
    const returningToEntry: boolean = this.isTargetUrl(restoredUrl, destinationUrl);
    let navigated: boolean = false;
    try {
      navigated = returningToEntry
        ? await this.navController.navigateBack(restoredUrl, {
          ...options,
          state: {...this.copyHistoryState(), ...options.state},
          replaceUrl: true,
        })
        : await this.navController.navigateForward(
          destinationUrl,
          this.withNavigationOrigin({...options, replaceUrl: false}),
        );
      return navigated ? 'moved' : 'cancelled';
    } finally {
      if (!navigated) await this.waitForHistoryRestoration(collapseResult);
    }
  }

  private tryPopRoute(options: AppNavigationOptions): Promise<NavigationPopOutcome> {
    if (!this.resolveNavigationOriginUrl()) return Promise.resolve('unavailable');

    return new Promise<NavigationPopOutcome>((resolve): void => {
      let navigationId: number | null = null;
      let settled: boolean = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const subscription: Subscription = this.router.events.subscribe((event: RouterEvent): void => {
        if (event instanceof NavigationStart) {
          if (navigationId === null) navigationId = event.id;
          return;
        }
        if (navigationId === null) return;

        if (event instanceof NavigationEnd && event.id === navigationId) {
          finish('moved');
        } else if (
          (event instanceof NavigationCancel || event instanceof NavigationError)
          && event.id === navigationId
        ) {
          finish('cancelled');
        } else if (event instanceof NavigationSkipped && event.id === navigationId) {
          finish('unavailable');
        }
      });
      const finish = (outcome: NavigationPopOutcome): void => {
        if (settled) return;

        settled = true;
        if (timeoutId !== null) clearTimeout(timeoutId);
        subscription.unsubscribe();
        resolve(outcome);
      };

      timeoutId = setTimeout(
        (): void => finish(navigationId === null ? 'unavailable' : 'cancelled'),
        AppNavigationService.POP_NAVIGATION_TIMEOUT_MS,
      );
      this.navController.back({animated: options.animated, animationDirection: 'back'});
    });
  }

  private trackNavigationOrigin(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      void this.resetAndroidBackConfirmation();
      if (event.navigationTrigger === 'imperative'
        && this.isTargetUrl(this.resolveCurrentUrl(), this.router.url)) {
        this.activateBrowserRootBoundary();
      }
      const navigation = this.router.getCurrentNavigation();
      if (
        event.navigationTrigger === 'imperative'
        && navigation
        && !navigation.extras.skipLocationChange
      ) {
        const originUrl: string | null = navigation.extras.replaceUrl
          ? (this.isExplicitNavigationInProgress ? null : this.resolveNavigationOriginUrl())
          : this.resolveCurrentUrl();
        if (originUrl) this.pendingNavigationOrigins.set(event.id, originUrl);
      }
      return;
    }

    if (event instanceof NavigationEnd) {
      const originUrl: string | undefined = this.pendingNavigationOrigins.get(event.id);
      this.pendingNavigationOrigins.delete(event.id);
      if (originUrl) this.writeNavigationOrigin(originUrl);
      this.persistCompletedFlowIds();
      this.syncRouteFlow();
      this.initializeBrowserRootBoundary();
      this.finishBrowserRootBoundaryPop();
      this.persistBrowserRootBoundaryState();
    } else if (event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped) {
      this.pendingNavigationOrigins.delete(event.id);
      if (event instanceof NavigationSkipped) {
        this.finishBrowserRootBoundaryPop();
      }
    }
  }

  private writeNavigationOrigin(originUrl: string): void {
    const state: Record<string, unknown> = this.copyHistoryState();
    if (AppNavigationService.NAVIGATION_FROM_STATE_KEY in state) return;

    state[AppNavigationService.NAVIGATION_FROM_STATE_KEY] = originUrl;
    this.location.replaceState(this.resolveCurrentUrl(), undefined, state);
  }

  private syncRouteFlow(): void {
    if (typeof history === 'undefined') return;

    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let flowRootUrl: string | null = null;
    while (route) {
      const configuredFlowRoot: unknown = route.data[AppNavigationService.ROUTE_FLOW_DATA_KEY];
      if (typeof configuredFlowRoot === 'string') flowRootUrl = configuredFlowRoot;
      route = route.firstChild;
    }
    if (!flowRootUrl) return;

    const flowId: string = normalizeStateRoutePath(flowRootUrl);
    const flowChanged: boolean = this.resolveCurrentFlowId() !== flowId;
    const completionRemoved: boolean = this.completedFlowIds.delete(flowId);
    if (!flowChanged && !completionRemoved) return;

    const state: Record<string, unknown> = this.copyHistoryState();
    state[AppNavigationService.FLOW_ID_STATE_KEY] = flowId;
    this.writeCompletedFlowIds(state);
    this.location.replaceState(this.resolveCurrentUrl(), undefined, state);
  }

  private persistCompletedFlowIds(): void {
    if (typeof history === 'undefined') return;

    const state: Record<string, unknown> = this.copyHistoryState();
    if (
      this.completedFlowIds.size === 0
      && !(AppNavigationService.COMPLETED_FLOW_IDS_STATE_KEY in state)
    ) {
      return;
    }

    this.writeCompletedFlowIds(state);
    this.location.replaceState(this.resolveCurrentUrl(), undefined, state);
  }

  private resolveBackFallback(currentUrl: string, fallbackUrl: string | undefined): string {
    return (fallbackUrl === undefined ? null : normalizeStateRouteUrl(fallbackUrl))
      ?? this.resolveEntryFallbackUrl()
      ?? resolveStateRouteFallbackUrl(currentUrl)
      ?? STATE_ROUTE_ROOT;
  }

  private resolveNavigationOriginUrl(): string | null {
    const state: unknown = typeof history === 'undefined' ? null : history.state;
    if (!this.isRecord(state)) return null;

    const originUrl: unknown = state[AppNavigationService.NAVIGATION_FROM_STATE_KEY];
    return typeof originUrl === 'string' && isInternalStateRouteUrl(originUrl)
      ? normalizeStateRouteUrl(originUrl)
      : null;
  }

  private resolveCurrentFlowId(): string | null {
    return typeof history === 'undefined' ? null : this.readFlowId(history.state);
  }

  private resolveEntryFallbackUrl(): string | null {
    const state: unknown = typeof history === 'undefined' ? null : history.state;
    if (!this.isRecord(state)) return null;

    const fallbackUrl: unknown = state[AppNavigationService.ENTRY_FALLBACK_URL_STATE_KEY];
    return typeof fallbackUrl === 'string' && isInternalStateRouteUrl(fallbackUrl)
      ? normalizeStateRouteUrl(fallbackUrl)
      : null;
  }

  private readFlowId(state: unknown): string | null {
    if (!this.isRecord(state)) return null;

    const flowId: unknown = state[AppNavigationService.FLOW_ID_STATE_KEY];
    return typeof flowId === 'string' && isInternalStateRouteUrl(flowId)
      ? normalizeStateRoutePath(flowId)
      : null;
  }

  private readCompletedFlowIds(state: unknown): readonly string[] {
    if (!this.isRecord(state)) return [];

    const flowIds: unknown = state[AppNavigationService.COMPLETED_FLOW_IDS_STATE_KEY];
    return Array.isArray(flowIds)
      ? flowIds.filter((flowId: unknown): flowId is string => (
        typeof flowId === 'string' && isInternalStateRouteUrl(flowId)
      )).map((flowId: string): string => normalizeStateRoutePath(flowId))
      : [];
  }

  private writeCompletedFlowIds(state: Record<string, unknown>): void {
    if (this.completedFlowIds.size > 0) {
      state[AppNavigationService.COMPLETED_FLOW_IDS_STATE_KEY] = [...this.completedFlowIds];
    } else {
      delete state[AppNavigationService.COMPLETED_FLOW_IDS_STATE_KEY];
    }
  }

  private writeEntryFallbackUrl(fallbackUrl: string): void {
    const state: Record<string, unknown> = this.copyHistoryState();
    state[AppNavigationService.ENTRY_FALLBACK_URL_STATE_KEY] = fallbackUrl;
    this.location.replaceState(this.resolveCurrentUrl(), undefined, state);
  }

  private withNavigationOrigin(options: AppNavigationOptions): AppNavigationOptions {
    if (options.skipLocationChange) return options;

    const state: Record<string, unknown> = this.isRecord(options.state) ? {...options.state} : {};
    const originUrl: string | null = options.replaceUrl
      ? this.resolveNavigationOriginUrl()
      : this.resolveCurrentUrl();
    if (originUrl) state[AppNavigationService.NAVIGATION_FROM_STATE_KEY] = originUrl;
    const flowId: string | null = this.resolveCurrentFlowId();
    if (flowId) state[AppNavigationService.FLOW_ID_STATE_KEY] = flowId;
    const entryFallbackUrl: string | null = this.resolveEntryFallbackUrl();
    if (entryFallbackUrl) {
      state[AppNavigationService.ENTRY_FALLBACK_URL_STATE_KEY] = entryFallbackUrl;
    }
    if (options.replaceUrl) this.writeCompletedFlowIds(state);
    return originUrl || flowId || entryFallbackUrl || options.replaceUrl ? {...options, state} : options;
  }

  private withReturnNavigationOrigin(
    targetUrl: string,
    options: AppNavigationOptions,
  ): AppNavigationOptions {
    const replacementOptions: AppNavigationOptions = this.withNavigationOrigin({...options, replaceUrl: true});
    if (!this.isRecord(replacementOptions.state)) return replacementOptions;

    const originUrl: unknown = replacementOptions.state[AppNavigationService.NAVIGATION_FROM_STATE_KEY];
    if (typeof originUrl !== 'string' || !this.isTargetUrl(originUrl, targetUrl)) return replacementOptions;

    const state: Record<string, unknown> = {...replacementOptions.state};
    delete state[AppNavigationService.NAVIGATION_FROM_STATE_KEY];
    return {...replacementOptions, state};
  }

  private resolveNavigationTargetUrl(
    url: string | UrlTree | unknown[],
    options: AppNavigationOptions,
  ): string {
    let targetUrl: string;
    if (typeof url === 'string') {
      targetUrl = url;
    } else if (Array.isArray(url)) {
      targetUrl = this.router.serializeUrl(this.router.createUrlTree(url));
    } else {
      targetUrl = this.router.serializeUrl(url);
    }
    return this.resolveReturnTargetUrl(targetUrl, options);
  }

  private resolveReturnTargetUrl(url: string, options: AppNavigationOptions): string {
    const targetTree: UrlTree = this.router.parseUrl(normalizeStateRouteUrl(url));
    if (options.queryParams !== undefined) {
      targetTree.queryParams = {...options.queryParams};
    }
    if (options.fragment !== undefined) {
      targetTree.fragment = options.fragment;
    }
    return normalizeStateRouteUrl(this.router.serializeUrl(targetTree));
  }

  private canPopOriginTowardsTarget(currentUrl: string, originUrl: string, targetUrl: string): boolean {
    if (this.isTargetUrl(originUrl, targetUrl)) return true;
    if (this.isTargetUrl(targetUrl, STATE_ROUTE_ROOT)) return true;
    return this.isRouteWithinTarget(currentUrl, targetUrl)
      && this.isRouteWithinTarget(originUrl, targetUrl);
  }

  private isRouteWithinTarget(url: string, targetUrl: string): boolean {
    if (this.hasUrlSuffix(targetUrl)) return false;

    const path: string = normalizeStateRoutePath(url);
    const targetPath: string = normalizeStateRoutePath(targetUrl);
    return path === targetPath || path.startsWith(`${targetPath}/`);
  }

  private isTargetUrl(url: string, targetUrl: string): boolean {
    const normalizedUrl: string = normalizeStateRouteUrl(url);
    const normalizedTargetUrl: string = normalizeStateRouteUrl(targetUrl);
    return this.hasUrlSuffix(normalizedTargetUrl)
      ? normalizedUrl === normalizedTargetUrl
      : normalizeStateRoutePath(normalizedUrl) === normalizeStateRoutePath(normalizedTargetUrl);
  }

  private hasUrlSuffix(url: string): boolean {
    return url.includes('?') || url.includes('#');
  }

  private async runExplicitNavigation(action: () => Promise<boolean>): Promise<boolean> {
    this.explicitNavigationDepth += 1;
    try {
      return await action();
    } finally {
      this.explicitNavigationDepth -= 1;
    }
  }

  // TODO: Remove this cleanup after the first production release with AppNavigationService.
  private clearDeprecatedStateRouterData(): void {
    if (typeof history === 'undefined' || typeof window === 'undefined') return;

    try {
      window.sessionStorage.removeItem(AppNavigationService.DEPRECATED_STORAGE_KEY);
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }

    const state: Record<string, unknown> = this.copyHistoryState();
    if (!(AppNavigationService.DEPRECATED_BROWSER_STATE_KEY in state)) return;

    delete state[AppNavigationService.DEPRECATED_BROWSER_STATE_KEY];
    history.replaceState(state, '', window.location.href);
  }

  private initializeBrowserRootBoundary(): void {
    if (!this.supportsBrowserRootBoundary() || typeof history === 'undefined' || typeof window === 'undefined') {
      return;
    }
    if (this.browserRootBoundaryInitialized) return;

    const boundaryState: BrowserRootBoundaryState | null = this.readBrowserRootBoundaryState(history.state);
    if (boundaryState === 'active') {
      this.browserRootBoundaryInitialized = true;
      return;
    }
    if (boundaryState === 'base') {
      this.browserRootBoundaryInitialized = true;
      this.activateBrowserRootBoundary();
      return;
    }

    const currentUrl: string = this.resolveCurrentUrl();
    const currentState: Record<string, unknown> = this.copyHistoryState();
    const currentPosition: StateHistoryRouterPosition | null = readStateHistoryRouterPosition(currentState);
    if (!currentPosition) return;

    const baseState: Record<string, unknown> = {
      ...createStateHistoryRouterState({
        navigationId: currentPosition.navigationId,
        pageId: currentPosition.pageId - 1,
      }),
      [AppNavigationService.ROOT_BOUNDARY_STATE_KEY]: 'base',
    };
    history.replaceState(baseState, '', STATE_ROUTE_ROOT);

    const activeState: Record<string, unknown> = {
      ...currentState,
      [AppNavigationService.ROOT_BOUNDARY_STATE_KEY]: 'active',
    };
    if (!this.isTargetUrl(currentUrl, STATE_ROUTE_ROOT)) {
      activeState[AppNavigationService.NAVIGATION_FROM_STATE_KEY] = STATE_ROUTE_ROOT;
    }
    history.pushState(activeState, '', currentUrl);
    this.browserRootBoundaryInitialized = true;
  }

  private activateBrowserRootBoundary(): void {
    if (!this.supportsBrowserRootBoundary() || typeof history === 'undefined') return;
    if (this.readBrowserRootBoundaryState(history.state) !== 'base') return;

    const baseState: Record<string, unknown> = this.copyHistoryState();
    const basePosition: StateHistoryRouterPosition | null = readStateHistoryRouterPosition(baseState);
    if (!basePosition) {
      console.warn('[AppNavigation] browser root boundary base has no Angular router history state');
      return;
    }

    const activeState: Record<string, unknown> = {
      ...baseState,
      ...createStateHistoryRouterState({
        navigationId: basePosition.navigationId,
        pageId: basePosition.pageId + 1,
      }),
      [AppNavigationService.ROOT_BOUNDARY_STATE_KEY]: 'active',
    };
    history.pushState(activeState, '', STATE_ROUTE_ROOT);
  }

  private finishBrowserRootBoundaryPop(): void {
    if (!this.isTargetUrl(this.resolveCurrentUrl(), STATE_ROUTE_ROOT)) return;
    if (!this.supportsBrowserRootBoundary() || typeof history === 'undefined') return;
    if (this.readBrowserRootBoundaryState(history.state) !== 'base') return;

    const currentState: Record<string, unknown> = this.copyHistoryState();
    const currentPosition: StateHistoryRouterPosition | null = readStateHistoryRouterPosition(currentState);
    if (!currentPosition) return;

    // NavigationEnd already synchronized Angular with this page id. Keep it on
    // the active entry so a later rejected Back can restore the correct position.
    history.replaceState({
      ...createStateHistoryRouterState({...currentPosition, pageId: currentPosition.pageId - 1}),
      [AppNavigationService.ROOT_BOUNDARY_STATE_KEY]: 'base',
    }, '', STATE_ROUTE_ROOT);
    history.pushState({
      ...currentState,
      [AppNavigationService.ROOT_BOUNDARY_STATE_KEY]: 'active',
    }, '', STATE_ROUTE_ROOT);
  }

  private persistBrowserRootBoundaryState(): void {
    if (!this.supportsBrowserRootBoundary() || typeof history === 'undefined') return;
    if (!this.browserRootBoundaryInitialized) return;
    if (this.readBrowserRootBoundaryState(history.state) !== null) return;

    const state: Record<string, unknown> = this.copyHistoryState();
    state[AppNavigationService.ROOT_BOUNDARY_STATE_KEY] = 'active';
    this.location.replaceState(this.resolveCurrentUrl(), undefined, state);
  }

  private readBrowserRootBoundaryState(state: unknown): BrowserRootBoundaryState | null {
    if (!this.isRecord(state)) return null;

    const boundaryState: unknown = state[AppNavigationService.ROOT_BOUNDARY_STATE_KEY];
    return boundaryState === 'active' || boundaryState === 'base' ? boundaryState : null;
  }

  private supportsBrowserRootBoundary(): boolean {
    return this.platformService.isBrowser && !this.platformService.isDevice;
  }

  private resolveCurrentUrl(): string {
    const url: string = typeof window === 'undefined'
      ? this.router.url
      : `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return normalizeStateRouteUrl(url);
  }

  private copyHistoryState(): Record<string, unknown> {
    const state: unknown = typeof history === 'undefined' ? null : history.state;
    return this.isRecord(state) ? {...state} : {};
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }
}
